import { AdminAnalyticsPage } from './pages/admin/AdminAnalyticsPage'
import { InviteHandoffListener } from './auth/inviteHandoff'
import { DashboardRedirect } from './components/DashboardRedirect'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import { TaxonomyProvider } from './taxonomy/TaxonomyContext'
import { RequireAuth } from './auth/RequireAuth'
import { Layout } from './components/Layout'
import { AdminLoginPage } from './pages/admin/AdminLoginPage'
import { AdminVerificationsPage } from './pages/admin/AdminVerificationsPage'
import { AdminProvidersPage } from './pages/admin/AdminProvidersPage'
import { AdminSyncPage } from './pages/admin/AdminSyncPage'
import { AdminEmailTemplatesPage } from './pages/admin/AdminEmailTemplatesPage'
import { AdminStudentImportPage } from './pages/admin/AdminStudentImportPage'
import { AdminTutorImportPage } from './pages/admin/AdminTutorImportPage'
import { AdminBulkUploadsPage } from './pages/admin/AdminBulkUploadsPage'
import { InvitationPage } from './pages/InvitationPage'
import { VioletDotsReturnPage } from './pages/VioletDotsReturnPage'
import { AdminAggregatorsPage } from './pages/admin/AdminAggregatorsPage'
import { AdminTeamPage } from './pages/admin/AdminTeamPage'
import { AdminReportsPage } from './pages/admin/AdminReportsPage'
import { CertificationPage } from './pages/CertificationPage'
import { AdminGate } from './pages/admin/adminAccess'
import { AggregatorPortalAuthPage } from './pages/aggregator-portal/AggregatorPortalAuthPage'
import { AggregatorPortalPage } from './pages/aggregator-portal/AggregatorPortalPage'
import { AggregatorOrgPage } from './pages/aggregator-portal/AggregatorOrgPage'
import { CoordinatorInvitePage } from './pages/aggregator-portal/CoordinatorInvitePage'
import { JoinWithCodePage } from './pages/aggregator-portal/JoinWithCodePage'
import { AddStudentProfilePage } from './pages/register/AddStudentProfilePage'
import { AddTutorProfilePage } from './pages/register/AddTutorProfilePage'
import { HomePage } from './pages/HomePage'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { TermsPage } from './pages/TermsPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { ProfilePage } from './pages/ProfilePage'
import { NeedsPage } from './pages/NeedsPage'
import { ServicesPage } from './pages/ServicesPage'
import { ConnectionsPage } from './pages/ConnectionsPage'
import { EngagementsPage } from './pages/EngagementsPage'
import { TrustPage } from './pages/TrustPage'
import { GuardianPage } from './pages/GuardianPage'
import { TutorListingPage } from './pages/TutorListingPage'
import { GrowthPage } from './pages/GrowthPage'
import { MapPage } from './pages/MapPage'
import { MarketplacePage } from './pages/MarketplacePage'
import { NotificationsPage } from './pages/NotificationsPage'

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Outside RequireAuth: /api/taxonomy is public, and the registration wizard needs the
            same vocabulary the matcher runs on before anyone has signed in. */}
        <TaxonomyProvider>
        {/* Hears a parent's invite link opened in another tab, and takes it here if signed out. */}
        <InviteHandoffListener />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Passwords and confirmation links are gone - sign-in is a join code now. These keep
              links in old emails landing somewhere useful. */}
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />
          <Route path="/verify-email" element={<Navigate to="/login" replace />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* A bulk-uploaded person's Confirm / Reject invitation, from their email (V51). */}
          <Route path="/invitation/:token" element={<InvitationPage />} />
          {/* Violet Dots' "Back to Yellow Dots" button - signs the person back in (V53). */}
          <Route path="/partner/violetdots/return" element={<VioletDotsReturnPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* Ops. Outside RequireAuth and outside Layout: an admin is not a participant, so it
              has its own token, its own sign-in and none of the participant chrome. */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          {/* Each screen behind its admin role (V42) - see adminAccess. */}
          <Route path="/admin" element={<AdminGate page="verifications"><AdminVerificationsPage /></AdminGate>} />
          <Route path="/admin/providers" element={<AdminGate page="providers"><AdminProvidersPage /></AdminGate>} />
          <Route path="/admin/sync" element={<AdminGate page="sync"><AdminSyncPage /></AdminGate>} />
          <Route path="/admin/email-templates"element={<AdminGate page="emailTemplates"><AdminEmailTemplatesPage /></AdminGate>} />
          <Route path="/admin/analytics" element={<AdminGate page="analytics"><AdminAnalyticsPage /></AdminGate>} />
          <Route path="/admin/bulk-upload/seekers" element={<AdminGate page="bulkUpload"><AdminStudentImportPage /></AdminGate>} />
          <Route path="/admin/bulk-upload/tutors" element={<AdminGate page="bulkUpload"><AdminTutorImportPage /></AdminGate>} />
          <Route path="/admin/bulk-upload/report" element={<AdminGate page="bulkUpload"><AdminBulkUploadsPage /></AdminGate>} />
          {/* The old separate screens, now tabs of Bulk upload. */}
          <Route path="/admin/bulk-upload" element={<Navigate to="/admin/bulk-upload/seekers" replace />} />
          <Route path="/admin/students" element={<Navigate to="/admin/bulk-upload/seekers" replace />} />
          <Route path="/admin/tutors" element={<Navigate to="/admin/bulk-upload/tutors" replace />} />
          <Route path="/admin/bulk-uploads" element={<Navigate to="/admin/bulk-upload/report" replace />} />
          <Route path="/admin/aggregators" element={<AdminGate page="aggregators"><AdminAggregatorsPage /></AdminGate>} />
          <Route path="/admin/reports" element={<AdminGate page="trust"><AdminReportsPage /></AdminGate>} />
          <Route path="/admin/team" element={<AdminGate page="team"><AdminTeamPage /></AdminGate>} />

          {/* The aggregator portal. Its own session, like ops - an aggregator is an organisation, not
              a participant, so it is outside RequireAuth and Layout. */}
          <Route path="/aggregator-portal/login" element={<AggregatorPortalAuthPage mode="login" />} />
          <Route path="/aggregator-portal/register" element={<AggregatorPortalAuthPage mode="register" />} />
          <Route path="/aggregator-portal" element={<AggregatorPortalPage />} />
          <Route path="/aggregator-portal/org" element={<AggregatorOrgPage />} />
          <Route path="/aggregator-portal/invite/:token" element={<CoordinatorInvitePage />} />
          <Route path="/aggregator-portal/join" element={<JoinWithCodePage />} />

          <Route element={<RequireAuth />}>
            {/* Signed in, but outside Layout: opening a second profile is an onboarding flow, and
                the rail it would sit inside belongs to the profile being left behind. */}
            <Route path="/profile/add/tutor" element={<AddTutorProfilePage />} />
            <Route path="/profile/add/student" element={<AddStudentProfilePage />} />

            <Route element={<Layout />}>
              <Route path="/dashboard" element={<DashboardRedirect />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/certification" element={<CertificationPage />} />
              {/* The page's old address, before it was renamed - kept so a saved link still lands. */}
              <Route path="/credentials" element={<Navigate to="/certification" replace />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/marketplace" element={<MarketplacePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/needs" element={<NeedsPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/connections" element={<ConnectionsPage />} />
              <Route path="/engagements" element={<EngagementsPage />} />
              <Route path="/trust" element={<TrustPage />} />
              <Route path="/guardian" element={<GuardianPage />} />
              <Route path="/tutor-listing" element={<TutorListingPage />} />
              <Route path="/growth" element={<GrowthPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </TaxonomyProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
